import { useMemo} from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { convertFbaVolume } from '../../helpers/convertFbaVolume';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { E3M3_TO_MCF, M3_TO_BBL } from '../../helpers/conversionFactors';
import { fbaConversionFactors } from './fbaConversionFactors';

export const FBASummaryChart = ({ fba, units }) => {

  const user = useSelector(selectUser);
  const currentFBA = fba;
  const fbaSettings = currentFBA?.settings;

  const reservoirType = currentFBA?.settings?.type?.value;

  const darkMode = user?.value?.darkMode;

  const data = useMemo(() => getChartData(currentFBA), [currentFBA, units]); // eslint-disable-line

  function getChartData(fba) {
    try {
      const fbaData = fba?.fbaData;
      const fbaDiagnostics = fba?.diagnostics;
      const fbaWellboreModel = fba?.fbaWellboreModel;

      if (!fbaData || !fbaData?.length > 0 || !fbaDiagnostics || !fbaDiagnostics?.length > 0) return [];

      // Add Input Data traces (top left)
      let oilProductionTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x1',
        name: `Oil Rate (${units === 'metric' ? 'm3' : 'stb'}/d)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[1]
          },
          symbol: 'circle',
          size: 5
        },
        line: {
          width: 0
        },
      };
      let condensateProductionTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x1',
        name: `Condensate Rate (${units === 'metric' ? 'm3' : 'stb'}/d)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[1]
          },
          symbol: 'circle',
          size: 5
        },
        line: {
          width: 0
        },
      };
      let gasProductionTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x1',
        name: `Gas Rate (${units === 'metric' ? 'E3m3' : 'mscf'}/d)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[2]
          },
          symbol: 'diamond',
          size: 5
        },
      };
      let waterProductionTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x1',
        name: `Water Rate (${units === 'metric' ? 'm3' : 'stb'}/d)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: (darkMode ? chartColors[8] : chartColors[0])
          },
          symbol: 'square',
          size: 5
        },
      };
      let pressureTrace = {
        x: [],
        y: [],
        type: 'scatter',
        yaxis: 'y2',
        xaxis: 'x1',
        name: `Pwh (${units === 'metric' ? 'kPa' : 'psia'})`,
        line: {
          color: chartColors[3],
          dash: '10px 4px'
        }
      };
      let fbhpTrace = {
        x: [],
        y: [],
        type: 'scatter',
        yaxis: 'y2',
        xaxis: 'x1',
        name: `FBHP (${units === 'metric' ? 'kPa' : 'psia'})`,
        line: {
          color: chartColors[8],
          dash: '10px 4px'
        }
      };
      let gorTrace = {
        x: [],
        y: [],
        type: 'scatter',
        yaxis: 'y2',
        xaxis: 'x1',
        name: `GOR (${units === 'metric' ? 'E3m3/m3' : 'scf/stb'}/d)`,
        line: {
          color: chartColors[4],
          dash: '6px 4px',
          width: 2
        }
      };
      let cgrTrace = {
        x: [],
        y: [],
        type: 'scatter',
        yaxis: 'y2',
        xaxis: 'x1',
        name: `CGR (${units === 'metric' ? 'm3/E3m3' : 'stb/mscf'}/d)`,
        line: {
          color: chartColors[4],
          dash: '6px 4px',
          width: 2
        }
      };
      
      // Add Diagnostic traces (top right)
      let waterRNPTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x2',
        yaxis: 'y3',
        name: 'RNP (x1000)',
        marker: {
          color: chartColors[2],
          line: {
            width: 1,
            color: chartColors[2]
          },
          symbol: 'circle',
          size: 5
        },
        line: {
          width: 0
        },
        opacity: 0.75
      };
      let waterRNPPrimeTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x2',
        yaxis: 'y3',
        name: 'RNP\' (x1000)',
        marker: {
          color: chartColors[8],
          line: {
            width: 1,
            color: chartColors[8]
          },
          symbol: 'circle',
          size: 5
        },
        line: {
          width: 0
        },
        opacity: 0.75
      };
      let linearFlowFitTrace = {
        x: [],
        y: [],
        type: 'scatter',
        mode: 'lines+markers',
        xaxis: 'x2',
        yaxis: 'y3',
        name: 'RNP Fit',
        // opacity: 0.75,
        line: {
          width: 3,
          color: chartColors[3]
        },
        marker: {
          color: 'transparent',
          line: {
            width: 0,
            color: 'transparent'
          },
          symbol: 'circle',
          size: 6
        }
      };
      let fractureDepletionFitTrace = {
        x: [],
        y: [],
        type: 'scatter',
        mode: 'lines+markers',
        xaxis: 'x2',
        yaxis: 'y3',
        name: 'RNP\' Fit',
        // opacity: 0.75,
        line: {
          width: 3,
          color: chartColors[5]
        },
        marker: {
          color: 'transparent',
          line: {
            width: 0,
            color: 'transparent'
          },
          symbol: 'circle',
          size: 6
        }
      };

      // Add History Match trace
      let actualWaterRateTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x3',
        yaxis: 'y4',
        name: `Water Rate Actual (${units === 'metric' ? 'm3' : 'stb'}/d)`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: (darkMode ? chartColors[8] : chartColors[0])
          },
          symbol: 'square',
          size: 5,
        },
        opacity: 0.75
      };
      let fractureWaterRateTrace = {
        x: [],
        y: [],
        type: 'scatter',
        mode: 'line',
        yaxis: 'y4',
        xaxis: 'x3',
        name: `Water Rate Model (${units === 'metric' ? 'm3' : 'stb'}/d)`,
        line: {
          width: 3,
          color: (darkMode ? chartColors[8] : chartColors[0])
        }
      };
      let actualOilRateTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        name: `Oil Rate Actual (${units === 'metric' ? 'm3' : 'stb'}/d)`,
        xaxis: 'x3',
        yaxis: 'y4',
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[1]
          },
          symbol: 'circle',
          size: 5,
        },
        opacity: 0.75,
        line: {
          width: 0
        }
      };
      let fractureOilRateTrace = {
        x: [],
        y: [],
        type: 'scatter',
        mode: 'line',
        yaxis: 'y4',
        xaxis: 'x3',
        name: `Oil Rate Model (${units === 'metric' ? 'm3' : 'stb'}/d)`,
        line: {
          width: 3,
          color: chartColors[1]
        }
      };
      let actualCondensateRateTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        name: `Cond. Rate Actual (${units === 'metric' ? 'm3' : 'stb'}/d)`,
        xaxis: 'x3',
        yaxis: 'y4',
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[1]
          },
          symbol: 'circle',
          size: 5,
        },
        opacity: 0.75,
        line: {
          width: 0
        }
      };
      let fractureCondensateRateTrace = {
        x: [],
        y: [],
        type: 'scatter',
        mode: 'line',
        yaxis: 'y4',
        xaxis: 'x3',
        name: `Cond. Rate Model (${units === 'metric' ? 'm3' : 'stb'}/d)`,
        line: {
          width: 3,
          color: chartColors[1]
        }
      };
      let actualGasRateTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        name: `Gas Rate Actual (${units === 'metric' ? 'E3m3' : 'mscf'}/d)`,
        xaxis: 'x3',
        yaxis: 'y5',
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[2]
          },
          symbol: 'diamond',
          size: 5,
        },
        opacity: 0.75,
        line: {
          width: 0
        }
      };
      let fractureGasRateTrace = {
        x: [],
        y: [],
        type: 'scatter',
        mode: 'line',
        xaxis: 'x3',
        yaxis: 'y5',
        name: `Gas Rate Model (${units === 'metric' ? 'E3m3' : 'mscf'}/d)`,
        line: {
          width: 3,
          color: chartColors[2]
        }
      };
      let actualCumWaterTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        xaxis: 'x4',
        yaxis: 'y6',
        name: `Water Cum. Actual (${units === 'metric' ? 'm3' : 'stb'})`,
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: (darkMode ? chartColors[8] : chartColors[0])
          },
          symbol: 'square',
          size: 5,
        },
        opacity: 0.75,
        line: {
          width: 0
        }
      };
      let fractureCumWaterTrace = {
        x: [],
        y: [],
        type: 'scatter',
        mode: 'line',
        xaxis: 'x4',
        yaxis: 'y6',
        name: `Cum. Water Model (${units === 'metric' ? 'm3' : 'stb'})`,
        line: {
          width: 3,
          color: (darkMode ? chartColors[8] : chartColors[0]),
          dash: 'dash'
        },
      };
      let actualCumOilTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        name: `Oil Cum. Actual (${units === 'metric' ? 'm3' : 'stb'})`,
        xaxis: 'x4',
        yaxis: 'y6',
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[1]
          },
          symbol: 'circle',
          size: 5,
        },
        opacity: 0.75,
        line: {
          width: 0
        }
      };
      let fractureCumOilTrace = {
        x: [],
        y: [],
        type: 'scatter',
        mode: 'line',
        xaxis: 'x4',
        yaxis: 'y6',
        name: `Oil Cum. Model (${units === 'metric' ? 'm3' : 'stb'})`,
        line: {
          width: 3,
          color: chartColors[1],
          dash: 'dash'
        }
      };
      let actualCumCondensateTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        name: `Cond. Cum. Actual (${units === 'metric' ? 'm3' : 'stb'})`,
        xaxis: 'x4',
        yaxis: 'y6',
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[1]
          },
          symbol: 'circle',
          size: 5,
        },
        opacity: 0.75,
        line: {
          width: 0
        }
      };
      let fractureCumCondensateTrace = {
        x: [],
        y: [],
        type: 'scatter',
        mode: 'line',
        xaxis: 'x4',
        yaxis: 'y6',
        name: `Cond. Cum. Model (${units === 'metric' ? 'm3' : 'stb'})`,
        line: {
          width: 3,
          color: chartColors[1],
          dash: 'dash'
        }
      };
      let actualCumGasTrace = {
        x: [],
        y: [],
        mode: 'markers',
        type: 'scatter',
        name: `Gas Cum. Actual (${units === 'metric' ? 'E3m3' : 'mscf'})`,
        xaxis: 'x4',
        yaxis: 'y7',
        marker: {
          color: 'transparent',
          line: {
            width: 1,
            color: chartColors[2]
          },
          symbol: 'diamond',
          size: 5,
        },
        opacity: 0.75,
        line: {
          width: 0
        },
      };
      let fractureCumGasTrace = {
        x: [],
        y: [],
        type: 'scatter',
        mode: 'line',
        xaxis: 'x4',
        yaxis: 'y7',
        name: `Gas Cum. Model (${units === 'metric' ? 'E3m3' : 'mscf'})`,
        line: {
          width: 3,
          color: chartColors[2],
          dash: 'dash'
        }
      };
      
      
      
      fbaData.forEach((d,i) => {
        if (d.oil > 0.1) {
          oilProductionTrace.x.push(d.days);
          oilProductionTrace.y.push(convertFbaVolume(d.oil, units, 'oil'));
        };
        if (d.condensate > 0.1) {
          condensateProductionTrace.x.push(d.days);
          condensateProductionTrace.y.push(convertFbaVolume(d.condensate, units, 'condensate'));
        };
        if (d.gas > 0.1) {
          gasProductionTrace.x.push(d.days);
          gasProductionTrace.y.push(convertFbaVolume(d.gas, units, 'gas'));
        };
        if (d.water > 0.1) {
          waterProductionTrace.x.push(d.days);
          waterProductionTrace.y.push(convertFbaVolume(d.water, units, 'water'));
        };
        pressureTrace.x.push(d.days);
        pressureTrace.y.push(units === 'metric' ? d?.pwh * fbaConversionFactors?.pwh : d?.pwh);
        gorTrace.x.push(d.days);
        gorTrace.y.push(units === 'metric' ? d.gor / (E3M3_TO_MCF / M3_TO_BBL) : d.gor);
        cgrTrace.x.push(d.days);
        cgrTrace.y.push(units === 'metric' ? d.cgr / (M3_TO_BBL / E3M3_TO_MCF) : d.cgr);
        waterRNPTrace.x.push(fbaDiagnostics?.[i]?.tmb);
        waterRNPTrace.y.push(fbaDiagnostics?.[i]?.rnp * (units === 'field' ? 1 : fbaConversionFactors['rnp']) * 1000); // RNP * 1000
        waterRNPPrimeTrace.x.push(fbaDiagnostics?.[i]?.tmb); 
        waterRNPPrimeTrace.y.push(fbaDiagnostics?.[i]?.rnpPrime * (units === 'field' ? 1 : fbaConversionFactors['rnp']) * 1000); // RNP' * 1000
        if (fbaDiagnostics?.[i]?.tmb < (currentFBA?.settings?.linearFlowEndTmb?.value + 0.2)) {
          linearFlowFitTrace.x.push(fbaDiagnostics?.[i]?.tmb);
          linearFlowFitTrace.y.push(fbaSettings?.linearFlowFitIntercept?.value * (units === 'field' ? 1 : fbaConversionFactors['linearFlowFitIntercept']) * Math.pow(fbaDiagnostics?.[i]?.tmb, 0.5));
        };
        if ((fbaDiagnostics?.[i]?.tmb > (currentFBA?.settings?.linearFlowEndTmb?.value - 0.2)) && (fbaDiagnostics?.[i]?.tmb < currentFBA?.breakthroughTmb + 0.2)) {
          fractureDepletionFitTrace.x.push(fbaDiagnostics?.[i]?.tmb);
          fractureDepletionFitTrace.y.push(fbaSettings?.fractureDepletionFitIntercept?.value * (units === 'field' ? 1 : fbaConversionFactors['fractureDepletionFitIntercept']) * fbaDiagnostics?.[i]?.tmb);
        };
        if (d.water > 0.1) {
          actualWaterRateTrace.x.push(d.days);
          actualWaterRateTrace.y.push(convertFbaVolume(d.water, units, 'water'));
        };
        if (currentFBA?.historyMatch?.[i]?.qTransOR > 0.1) {
          fractureWaterRateTrace.x.push(d.days);
          fractureWaterRateTrace.y.push(convertFbaVolume(currentFBA?.historyMatch?.[i]?.qTransOR, units, 'water'));
        };
        if (d.oil > 0.1) {
          actualOilRateTrace.x.push(d.days);
          actualOilRateTrace.y.push(convertFbaVolume(d.oil, units, 'oil'));
        };
        if (currentFBA?.historyMatch?.[i]?.qOilFracture > 0.1) {
          fractureOilRateTrace.x.push(d.days);
          fractureOilRateTrace.y.push(convertFbaVolume(currentFBA?.historyMatch?.[i]?.qOilFracture, units, 'oil'));
        };
        if (d.condensate > 0.1) {
          actualCondensateRateTrace.x.push(d.days);
          actualCondensateRateTrace.y.push(convertFbaVolume(d.condensate, units, 'condensate'));
        };
        if (currentFBA?.historyMatch?.[i]?.qCondensate > 0.1) {
          fractureCondensateRateTrace.x.push(d.days);
          fractureCondensateRateTrace.y.push(convertFbaVolume(currentFBA?.historyMatch?.[i]?.qCondensate, units, 'condensate'));
        };
        if (d.gas > 0.1) {
          actualGasRateTrace.x.push(d.days);
          actualGasRateTrace.y.push(convertFbaVolume(d.gas, units, 'gas'));
        };
        if (reservoirType === 1) {
          if (currentFBA?.historyMatch?.[i]?.qGasFracture > 0.1) {
            fractureGasRateTrace.x.push(d.days);
            fractureGasRateTrace.y.push(convertFbaVolume(currentFBA?.historyMatch?.[i]?.qGasFracture, units, 'gas'));
          };
        } else {
          if (currentFBA?.historyMatch?.[i]?.sepQGas > 0.1) {
            fractureGasRateTrace.x.push(d.days);
            fractureGasRateTrace.y.push(convertFbaVolume(currentFBA?.historyMatch?.[i]?.sepQGas, units, 'gas'));
          };
        };
        if (d.cumWater > 0.1) {
          actualCumWaterTrace.x.push(d.days);
          actualCumWaterTrace.y.push(convertFbaVolume(d.cumWater, units, 'water'));
        };
        if (currentFBA?.historyMatch?.[i]?.cumTransOR > 0.001) {
          fractureCumWaterTrace.x.push(d.days);
          fractureCumWaterTrace.y.push(convertFbaVolume(currentFBA?.historyMatch?.[i]?.cumTransOR * 1000, units, 'water'));
        };
        if (d.cumOil > 0.1) {
          actualCumOilTrace.x.push(d.days);
          actualCumOilTrace.y.push(convertFbaVolume(d.cumOil, units, 'oil'));
        };
        if (currentFBA?.historyMatch?.[i]?.cumBblOilFracture > 0.1) {
          fractureCumOilTrace.x.push(d.days);
          fractureCumOilTrace.y.push(convertFbaVolume(currentFBA?.historyMatch?.[i]?.cumBblOilFracture, units, 'oil'));
        };
        if (d.cumCondensate > 0.1) {
          actualCumCondensateTrace.x.push(d.days);
          actualCumCondensateTrace.y.push(convertFbaVolume(d.cumCondensate, units, 'condensate'));
        };
        if (currentFBA?.historyMatch?.[i]?.cumCondensate > 0.1) {
          fractureCumCondensateTrace.x.push(d.days);
          fractureCumCondensateTrace.y.push(convertFbaVolume(currentFBA?.historyMatch?.[i]?.cumCondensate, units, 'condensate'));
        };
        if (d.cumGas > 0.1) {
          actualCumGasTrace.x.push(d.days);
          actualCumGasTrace.y.push(convertFbaVolume(d.cumGas, units, 'gas'));
        };
        if (reservoirType === 1) {
          if (currentFBA?.historyMatch?.[i]?.cumBblGasFracture > 0.1) {
            fractureCumGasTrace.x.push(d.days);
            fractureCumGasTrace.y.push(convertFbaVolume(currentFBA?.historyMatch?.[i]?.cumBblGasFracture, units, 'gas'));
          };
        } else {
          if (currentFBA?.historyMatch?.[i]?.cumSepGas > 0.1) {
            fractureCumGasTrace.x.push(d.days);
            fractureCumGasTrace.y.push(convertFbaVolume(currentFBA?.historyMatch?.[i]?.cumSepGas, units, 'gas'));
          };
        };
        fbhpTrace.x.push(d.days);
        fbhpTrace.y.push(fbaWellboreModel?.fbhp?.[i] * (units === 'metric' ? fbaConversionFactors?.pwh : 1) || 0);
      });

      return [ 
        (reservoirType === 1 ? oilProductionTrace : condensateProductionTrace), 
        gasProductionTrace, 
        waterProductionTrace, 
        pressureTrace, 
        fbhpTrace, 
        (reservoirType === 1 ? gorTrace : cgrTrace), 
        waterRNPTrace, 
        waterRNPPrimeTrace, 
        linearFlowFitTrace, 
        fractureDepletionFitTrace,
        actualWaterRateTrace,
        fractureWaterRateTrace,
        (reservoirType === 1 ? actualOilRateTrace : actualCondensateRateTrace),
        (reservoirType === 1 ? fractureOilRateTrace : fractureCondensateRateTrace),
        actualGasRateTrace,
        fractureGasRateTrace,
        actualCumWaterTrace,
        fractureCumWaterTrace,
        (reservoirType === 1 ? actualCumOilTrace : actualCumCondensateTrace),
        (reservoirType === 1 ? fractureCumOilTrace : fractureCumCondensateTrace),
        actualCumGasTrace,
        fractureCumGasTrace
      ];
    } catch (error) {
      console.log(error);
      return [];
    };
  };

  const layout = {
    grid: {rows: 2, columns: 2, pattern: 'independent'},
    yaxis: { // Production y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `Gas (${units === 'metric' ? 'E3m3' : 'mscf'}/d), ${reservoirType === 1 ? 'Oil' : 'Condensate'} and Water (${units === 'metric' ? 'm3' : 'stb'}/d) Rate`,
        font: {
          size: 12
        },
      },
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0.55, 1],
    },
    xaxis: { // Days x axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Time (days)',
        standoff: 10,
        font: {
          size: 12
        }
      },
      domain: [0, 0.425],
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    yaxis2: { // Pwh y axis
      showgrid: false,
      linecolor: darkMode ? 'lightgrey' : 'black',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `Pwh (${units === 'metric' ? 'kPa' : 'psi'}) and ${reservoirType === 1 ? `GOR (${units === 'metric' ? 'E3m3' : 'scf'}/${units === 'metric' ? 'm3' : 'stb'})` : `CGR (${units === 'metric' ? 'm3' : 'stb'}/${units === 'metric' ? 'E3m3' : 'mscf'})`}`,
        font: {
          size: 12
        },
      },
      overlaying: 'y',
      side: 'right',
      anchor: 'x',
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0.55, 1],
    },
    xaxis2: { // RNP x axis
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Material Balance Time (days)',
        standoff: 10,
        font: {
          size: 12
        }
      },
      domain: [0.575, 1],
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      type: 'log',
      anchor: 'y1',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    yaxis3: { // RNP y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: 'RNP and RNP\' (x1000)',
        font: {
          size: 12
        },
      },
      type: 'log',
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0.55, 1],
      anchor: 'x2'
    },
    yaxis4: { // Water and Oil/Condy rate y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `Water and ${reservoirType === 1 ? 'Oil' : 'Condensate'} Rate (${units === 'metric' ? 'm3' : 'stb'}/d)`,
        font: {
          size: 12
        },
      },
      type: 'log',
      anchor: 'x3',
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0, 0.45],
      side: 'left'
    },
    yaxis5: { // Gas rate y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      showgrid: false,
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `Gas Rate (${units === 'metric' ? 'E3m3' : 'mscf'}/d)`,
        font: {
          size: 12
        },
      },
      type: 'log',
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0, 0.45],
      overlaying: 'y4',
      side: 'right',
      anchor: 'x3',
    },
    xaxis3: { // Days x axis
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Time (days)',
        standoff: 10,
        font: {
          size: 12
        } 
      },
      domain: [0, 0.425],
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y4',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    yaxis6: { // Cum Water and Oil/Condy y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `Cum. Water and ${reservoirType === 1 ? 'Oil' : 'Condensate'} (${units === 'metric' ? 'm3' : 'stb'})`,
        font: {
          size: 12
        },
      },
      type: 'log',
      anchor: 'x4',
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0, 0.45],
    },
    yaxis7: { // Cum Gas y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      showgrid: false,
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `Cum. Gas (${units === 'metric' ? 'E3m3' : 'mscf'})`,
        font: {
          size: 12
        },
      },
      type: 'log',
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0, 0.45],
      side: 'right',
      overlaying: 'y6',
      anchor: 'x4',
    },
    xaxis4: { // Days x axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: 'Time (days)',
        standoff: 10,
        font: {
          size: 12
        } 
      },
      anchor: 'y6',
      autorange: true,
      rangemode: 'nonnegative',
      domain: [0.575, 1],
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    hovermode: "x",
    hoverdistance: 100, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      font: {
        color: darkMode ? 'lightgrey' : 'grey'
      },
      orientation: "h",
      yanchor: "top",
      yref: 'paper',
      y: -0.075,
    },
    uirevision:'true',
    shapes: [ 
      { // Breakthrough line diagnostic
        type: 'line',
        yref: 'paper',
        ysizemode: 'scaled',
        xref: 'x2',
        x0: currentFBA?.breakthroughTmb,
        y0: 0.99,
        x1: currentFBA?.breakthroughTmb,
        y1: 0.55,
        fillcolor: '#333',
        opacity: 0.75,
        line:{
          color: (darkMode ? '#eee' : '#333'),
          width: 2,
          dash:'dot'
        }
      },
      { // linear flow end tmb diagnostic
        type: 'line',
        yref: 'paper',
        ysizemode: 'scaled',
        xref: 'x2',
        x0: currentFBA?.settings?.linearFlowEndTmb?.value,
        y0: 0.99,
        x1: currentFBA?.settings?.linearFlowEndTmb?.value,
        y1: 0.55,
        line:{
          color: 'purple',
          width: 2,
        }
      },
    ],
    annotations: [ // breakthrough tmb line annotation
      {
        x: Math.log10(currentFBA?.breakthroughTmb),
        y: 1,
        xref: 'x2',
        yref: 'paper',
        text: 'Breakthrough',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      },
      { // end of lin flow tmb
        x: Math.log10(currentFBA?.settings?.linearFlowEndTmb?.value),
        y: 1,
        xref: 'x2',
        yref: 'paper',
        text: 'End of Lin. Flow',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      },
      {
        text: "Input Data",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        xanchor: 'center',
        x: 0.225, //position in x domain
        y: 1.04, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: "RNP and RNP' Diagnostic",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        xanchor: 'center',
        x: 0.775, //position in x domain
        y: 1.04, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: "Fracture Model Rate",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        xanchor: 'center',
        x: 0.225, //position in x domain
        y: 0.475, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: "Fracture Model Cumulative",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        xanchor: 'center',
        x: 0.775, //position in x domain
        y: 0.475, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
    ],
    margin: {"t": 50, "l": 60, "r": 60, "b": 50},
    images: [
      {
        x: 0.025,
        y: 1.02,
        sizex: 0.12,
        sizey: 0.0275,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_${darkMode ? 'white' : 'primary'}.png`,
        xref: "paper",
        yref: "paper",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 1
      }
    ],
    plot_bgcolor: 'transparent',
    paper_bgcolor: (darkMode ? 'transparent' : '#fff')
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: currentFBA?.name + "-summary-" + (new Date().toISOString().split('T')[0]),
      height: 960,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%', minHeight: '960px', minWidth: '600px'}}
      elevation={0}
    />
  );

  
};
